/**
 * Preloader functionality
 * Handles the loading overlay with pulsing logo
 */

class Preloader {
  constructor() {
    this.preloader = document.getElementById('preloader');
    this.loadingLine = this.preloader ? this.preloader.querySelector('.preloader__loading-line-fill') : null;
    this.minDisplayTime = 1300; // Minimum time to show preloader (ms) - increased by 800ms
    this.startTime = Date.now();
    this.isContentLoaded = false;
    this.isResourcesLoaded = false;
    this.progress = 0;
    this.sessionKey = 'preloader_shown';
    
    this.init();
  }
  
  init() {
    if (!this.preloader) return;
    
    // Check if preloader has already been shown in this session
    if (sessionStorage.getItem(this.sessionKey)) {
      // If already shown, hide immediately without animation
      this.hidePreloaderImmediately();
      return;
    }
    
    // Mark that preloader has been shown in this session
    sessionStorage.setItem(this.sessionKey, 'true');
    
    // Set initial state
    document.body.style.overflow = 'hidden';
    
    // Start progress animation
    this.startProgressAnimation();
    
    // Listen for DOM content loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.isContentLoaded = true;
        this.updateProgress(60); // 60% when DOM is ready
        this.checkIfReady();
      });
    } else {
      this.isContentLoaded = true;
      this.updateProgress(60);
    }
    
    // Listen for all resources loaded
    if (document.readyState === 'complete') {
      this.isResourcesLoaded = true;
      this.updateProgress(100); // 100% when fully loaded
      this.checkIfReady();
    } else {
      window.addEventListener('load', () => {
        this.isResourcesLoaded = true;
        this.updateProgress(100);
        this.checkIfReady();
      });
    }
    
    // Fallback timeout to prevent infinite loading
    setTimeout(() => {
      this.updateProgress(100);
      this.hidePreloader();
    }, 5000);
  }
  
  startProgressAnimation() {
    if (!this.loadingLine) return;
    
    // Animate to 30% over first 500ms
    this.animateProgress(30, 500);
  }
  
  updateProgress(targetProgress) {
    if (!this.loadingLine) return;
    
    this.animateProgress(targetProgress, 300);
  }
  
  animateProgress(targetProgress, duration) {
    if (!this.loadingLine) return;
    
    const startProgress = this.progress;
    const progressDiff = targetProgress - startProgress;
    const startTime = Date.now();
    
    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out function
      const easeOut = 1 - Math.pow(1 - progress, 3);
      
      this.progress = startProgress + (progressDiff * easeOut);
      this.loadingLine.style.width = this.progress + '%';
      
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };
    
    animate();
  }
  
  checkIfReady() {
    if (this.isContentLoaded && this.isResourcesLoaded) {
      const elapsedTime = Date.now() - this.startTime;
      
      if (elapsedTime >= this.minDisplayTime) {
        this.hidePreloader();
      } else {
        // Wait for minimum display time
        setTimeout(() => {
          this.hidePreloader();
        }, this.minDisplayTime - elapsedTime);
      }
    }
  }
  
  hidePreloader() {
    if (!this.preloader) return;
    
    // Add hidden class for smooth transition
    this.preloader.classList.add('preloader--hidden');
    
    // Restore body scroll
    document.body.style.overflow = '';
    
    // Remove preloader from DOM after transition
    setTimeout(() => {
      if (this.preloader && this.preloader.parentNode) {
        this.preloader.remove();
      }
    }, 500);
    
    // Trigger custom event for other scripts that might need to know when preloader is hidden
    document.dispatchEvent(new CustomEvent('preloaderHidden'));
  }
  
  hidePreloaderImmediately() {
    if (!this.preloader) return;
    
    // Hide without animation for subsequent page loads
    this.preloader.style.display = 'none';
    
    // Restore body scroll immediately
    document.body.style.overflow = '';
    
    // Remove preloader from DOM immediately
    if (this.preloader.parentNode) {
      this.preloader.remove();
    }
    
    // Trigger custom event for other scripts that might need to know when preloader is hidden
    document.dispatchEvent(new CustomEvent('preloaderHidden'));
  }
  
  // Public method to manually hide preloader
  hide() {
    this.hidePreloader();
  }
}

// Initialize preloader immediately when script loads
(function() {
  // Early initialization for faster preloader display
  function initPreloaderEarly() {
    // Check if preloader has already been shown in this session
    if (sessionStorage.getItem('preloader_shown')) {
      return; // Don't show preloader if already shown
    }
    
    const preloader = document.getElementById('preloader');
    if (preloader && document.body) {
      document.body.style.overflow = 'hidden';
    }
  }
  
  // Try to initialize early if DOM is already parsed
  if (document.readyState !== 'loading') {
    initPreloaderEarly();
  } else {
    document.addEventListener('DOMContentLoaded', initPreloaderEarly);
  }
})();

// Initialize preloader when DOM is ready
function initializePreloader() {
  // Only initialize if preloader element exists and hasn't been shown yet
  if (document.getElementById('preloader') && !sessionStorage.getItem('preloader_shown')) {
    window.preloader = new Preloader();
  } else if (document.getElementById('preloader')) {
    // If preloader exists but already shown, hide it immediately
    const preloader = document.getElementById('preloader');
    preloader.style.display = 'none';
    document.body.style.overflow = '';
  }
}

// Handle both defer and normal script loading
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializePreloader);
} else {
  initializePreloader();
}

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = Preloader;
}